/**
 * BlockRegistry - Single source of truth for StrategyBlock data
 *
 * Sprint: sprint-4, Task: T005.2.1
 * Requirement: FR-P3-003 - BlockFlowCoordinator needs EntityRegistry<StrategyBlock>
 *
 * Responsibilities:
 * - Store StrategyBlock instances from @schema
 * - Provide CRUD operations (inherited from EntityRegistry)
 * - Notify listeners on changes (Observable pattern - inherited)
 * - Provide Block-specific utilities (registerMany, getAllIds, getStats, debug)
 *
 * Pattern: Instance-based, extends EntityRegistry<StrategyBlock>
 * Mirrors: ScreenRegistry structure
 *
 * Usage:
 * ```typescript
 * // Use default instance
 * import { blockRegistry } from '@/modules/reactFlow/services/BlockRegistry';
 * blockRegistry.register(block);
 *
 * // Or create your own instance
 * import { BlockRegistry } from '@/modules/reactFlow/services/BlockRegistry';
 * const registry = new BlockRegistry();
 * registry.register(block);
 *
 * // Subscribe to changes
 * const unsubscribe = registry.subscribe(blockId, () => {
 *   console.log('Block changed');
 * });
 *
 * // Update block
 * registry.update(blockId, { name: 'New Name' });
 * // → triggers callback
 * ```
 */

import type { StrategyBlock } from '@segmently/schema-models/strategy';
import { EntityRegistry } from '@modules/reactFlow/services/shared/EntityRegistry';

/**
 * Instance-based registry for StrategyBlock instances
 *
 * Extends EntityRegistry<StrategyBlock> (same pattern as ScreenRegistry)
 * Inherits CRUD operations and Observable pattern from base class.
 * Provides Block-specific utilities (registerMany, getAllIds, getStats, debug).
 */
export class BlockRegistry extends EntityRegistry<StrategyBlock> {
  constructor() {
    super(); // Call EntityRegistry constructor
  }

  // ============================================================================
  // Block-specific utilities
  // ============================================================================

  /**
   * Register multiple blocks at once (convenience method)
   */
  public registerMany(blocks: StrategyBlock[]): void {
    blocks.forEach(block => this.register(block));
    console.log(`[BlockRegistry] Registered ${blocks.length} blocks`);
  }

  /**
   * Get all block IDs (Block-specific utility)
   *
   * Returns entityId (PRIMARY KEY) for all blocks in registry
   */
  public getAllIds(): string[] {
    return this.getAll().map(block => block.entityId).filter((id): id is string => id !== undefined);
  }

  /**
   * Update block content (with notifications)
   *
   * Alias for update() with explicit intent - use this for updates that should
   * trigger node re-renders (name, description, action.edges, etc.).
   *
   * @param blockId - Block ID
   * @param updates - Partial updates
   */
  public updateContent(blockId: string, updates: Partial<StrategyBlock>): void {
    this.update(blockId, updates);
  }

  /**
   * Clear all blocks
   *
   * Inherited from EntityRegistry but explicitly exposed for testing.
   */
  public clear(): void {
    super.clear();
    console.log('[BlockRegistry] Cleared all blocks');
  }

  /**
   * Get registry stats (debugging utility)
   */
  public getStats(): {
    totalBlocks: number;
    totalListeners: number;
    blockIds: string[];
  } {
    return {
      totalBlocks: this.getAll().length,
      totalListeners: 0, // Cannot access private listeners from base class
      blockIds: this.getAllIds(),
    };
  }

  /**
   * Debug: Log registry state
   */
  public debug(): void {
    const stats = this.getStats();
    console.log('[BlockRegistry] Debug:', {
      ...stats,
      blocks: this.getAll().map(b => ({
        id: b.id,
        name: b.name,
        blockType: b.blockType,
        order: b.order,
      })),
    });
  }
}

/**
 * Export a default instance for convenience
 *
 * Note: Consumers can also create their own instances with `new BlockRegistry()`
 * Example:
 * ```typescript
 * // Use default instance
 * import { blockRegistry } from '@/modules/reactFlow/services/BlockRegistry';
 *
 * // Or create your own instance
 * import { BlockRegistry } from '@/modules/reactFlow/services/BlockRegistry';
 * const myRegistry = new BlockRegistry();
 * ```
 */
export const blockRegistry = new BlockRegistry();
